module Meevo.Plugins.Register {

    export enum TipMode {
        Unset,
        NoTip,
        Automatic,
        Manual
    }

    export class RegisterTipService  {
        constructor(
            public $q: ng.IQService,
            public $translate: angular.translate.ITranslateService,
            public dataService: Meevo.Components.BaseClasses.IDataService,
            public modalService: Meevo.Components.Modal.IModalService,
            public busyService: Meevo.Components.Busy.IBusyService,
        ) {
        }

        getTipModeText(tipMode: TipMode) {
            switch (tipMode) {
                case TipMode.Unset: return 'unset';
                case TipMode.NoTip: return 'no-tip';
                case TipMode.Automatic: return 'auto';
                case TipMode.Manual: return 'manual';
            }
        }

        private getTipMode(amount: number, percentage: number): TipMode {
            if (amount === undefined && percentage === undefined) {
                return TipMode.Unset;
            }

            if (amount === null && percentage === null) {
                return TipMode.Manual;
            }

            if (amount === 0 && percentage === 0) {
                return TipMode.NoTip;
            }

            return TipMode.Automatic;
        }

        isTipEligible(saleLines: Model.SaleLine[], isCreditMode: boolean, includeProductsInTips: boolean): boolean {
            if (isCreditMode) {
                return false;
            }

            if (saleLines.length === 0) {
                return true;
            }

            // saleline must be:
            // - a service or product or package service upgrade fee
            // - not a refund
            // - service must be allowed for tips
            // - employee or second employee must be allowed for tips

            const nonTippableClientPackageServicesUsedIds = this.getNonTippableClientPackageServicesUsedIds(saleLines);

            let isTipEligible = _(saleLines).any((saleLine: Model.SaleLine) =>
                this.isSaleLineEligibleForTip(saleLine, nonTippableClientPackageServicesUsedIds, includeProductsInTips) &&
                (saleLine.item && !saleLine.item.doNotAllowTips) &&
                (
                    (saleLine.employee && saleLine.employee.id && saleLine.employee.allowTips === true) ||
                    (saleLine.secondEmployee && saleLine.secondEmployee.id && saleLine.secondEmployee.allowTips === true)
                )
            );

            return isTipEligible;
        }

        isDeviceTipAvailable(registerContext: Meevo.Plugins.Register.RegisterContext, sale: Model.Sale, selectedTerminal, isCreditCardProcessingEnabled: boolean, isCreditMode: boolean, ignoreDeviceTipBypass: boolean = false): boolean {
            function log(message: string) {
                if (ignoreDeviceTipBypass) {
                    return;
                }
                console.log('DeviceTip', message);
            }

            if (!isCreditCardProcessingEnabled) {
                log('Credit Card Processing is disabled.');
                return false;
            }

            // terminal must be selected
            if (!selectedTerminal) {
                log('Credit Card Merchant Account or Terminal are not selected.');
                return false;
            }

            // at least one service (allowing tips) must exist
            if (!this.isTipEligible(sale.saleLines, isCreditMode, registerContext.includeProductsInTips)) {
                log('No tippable item (allowing tips) exists.')
                return false;
            }

            // hosted page
            if (registerContext.isHostedPage()) {
                log('Hosted Page active, no physical device present for tip.');
                return false;
            }

            // merchant account tips must be enabled
            if (!selectedTerminal.isTipEnable) {
                log('Device Tip is not enabled on Merchant Account.');
                return false;
            }

            if (sale.externalTipTransactionId) {
                log('Tippy Kiosk was engaged');
                return false;
            }

            if (!ignoreDeviceTipBypass) {
                // device tip must not be bypassed
                if (sale.deviceTipBypass === true) {
                    log('Device Tip was bypassed by user.');
                    return false;
                }
            }

            return true;
        }

        showTips(registerContext: Meevo.Plugins.Register.RegisterContext, sale: Model.Sale, modalType: RegisterModalTypeEnum = RegisterModalTypeEnum.Tip): ng.IPromise<any> {
            let currentTip = sale.tip;

            return registerContext.showModal(modalType).then(() => {
                if (sale.tip != currentTip && registerContext.hasPromoGiftCardWithService()) {
                    return registerContext.recalculateSale();
                }
            });
        }

        reassignTips(registerContext: Meevo.Plugins.Register.RegisterContext, sale: Model.Sale, saleLine: Model.SaleLine, isSecondEmployee: boolean) {
            if (!sale.tipAmount && !sale.tipPercentage) {
                let oldTipLine = _(sale.tipLines).find((tipLine: Model.TipLine) => !tipLine.isRefund && tipLine.originalEmployeeId == (isSecondEmployee ? saleLine.secondEmployeeId : saleLine.employeeId));
                let newTipLine = _(sale.tipLines).find((tipLine: Model.TipLine) => !tipLine.isRefund && tipLine.originalEmployeeId == (isSecondEmployee ? saleLine.secondEmployee.id : saleLine.employee.id));

                if (oldTipLine) {
                    if (newTipLine) {
                        newTipLine.amount = registerContext.addNormalizedNumbers(newTipLine.amount, oldTipLine.amount);
                        sale.tipLines = _(registerContext.sale.tipLines).without(oldTipLine);
                    } else {
                        let existingSaleLines = _(sale.saleLines).filter((existingSaleLine: Model.SaleLine) => existingSaleLine.employeeId == (isSecondEmployee ? saleLine.secondEmployeeId : saleLine.employeeId));
                        if (existingSaleLines.length === 1) {
                            oldTipLine.employee = saleLine.employee.allowTips ? saleLine.employee : registerContext.HouseEmployee;
                            oldTipLine.employeeId = saleLine.employee.allowTips ? saleLine.employee.id : registerContext.HouseEmployee.id;
                        }
                    }
                }
            }
        }

        deviceTip(registerContext: Meevo.Plugins.Register.RegisterContext, sale: Model.Sale, modalType: RegisterModalTypeEnum = RegisterModalTypeEnum.TipCreditCard, sectionType: RegisterSectionTypeEnum = null): ng.IPromise<any> {
            let defer = this.$q.defer();

            if (sale.recalculateSaleInProgress) {
                this.modalService.alert("MESSAGE_RECALCULATE_SALE_IN_PROGRESS");
                defer.reject();
                return defer.promise;
            }

            // check if device tip is available
            if (!registerContext.isDeviceTipAvailable()) {
                defer.resolve();
                return defer.promise;
            }

            this.busyService.start("MESSAGE_TERMINAL_TIP", true);

            const selectedTerminal = registerContext.selectedTerminal;
            const selectedDrawer = registerContext.selectedDrawer;

            let request = {
                merchantTerminalId: selectedTerminal.id,
                merchantTerminalSerialNumber: selectedTerminal.serialNumber,
                merchantAccountId: selectedTerminal.merchantAccountId,
                externalTerminalId: selectedTerminal.externalTerminalId,
                sale: sale,
                tipPromptTextPrefix: this.$translate.instant("LABEL_TIP_PROMPT_TEXT_PREFIX"),
                tipOtherText: this.$translate.instant("LABEL_TIP_OTHER_TEXT"),
            };

            // If this terminal is also paired (via TippyKioskId) with a Tippy kiosk currently engaged in an
            // active tip session, don't send the device tip prompt - it would interrupt the kiosk screen.
            registerContext.isPairedKioskLocked().then(isLocked => {
                if (isLocked) {
                    this.busyService.stop();
                    registerContext.showTerminalLockedAlert();
                    defer.reject();
                    return;
                }

                this.dataService
                    .customMethodData(`api/Drawer/${selectedDrawer.id}/TipPrompt`, "POST", request)
                    .then(response => {
                        if (response.status === 409) { // conflict
                            this.modalService.alert(response.data.responseStatus.message);
                            defer.reject();
                            return;
                        }

                        let tipResponse = response.data;

                        let deviceTip = tipResponse.tipAmount || 0;

                        if (deviceTip >= 0) {
                            sale.deviceTip = deviceTip;
                            sale.deviceServiceTotal = tipResponse.serviceTotal || 0;

                            if (tipResponse.tipPercentage) {
                                sale.tipAmount = null;
                                sale.tipPercentage = tipResponse.tipPercentage;
                                sale.deviceTipPercentage = tipResponse.tipPercentage;
                            } else {
                                sale.tipAmount = tipResponse.tipAmount;
                                sale.tipPercentage = null;
                                sale.deviceTipPercentage = null;
                            }

                            const isTipPromptFallback = tipResponse.isTipPromptError &&
                                registerContext.selectedMerchantAccount.isHostedPageAutomaticallySwitch;

                            if (isTipPromptFallback || (deviceTip > 0 && sectionType !== RegisterSectionTypeEnum.CreditSingleCard)) {
                                this.busyService.stop();

                                if (modalType !== RegisterModalTypeEnum.TipChangeDue && modalType !== RegisterModalTypeEnum.TipCreditCard) {
                                    modalType = RegisterModalTypeEnum.TipCreditCard;
                                }

                                this.showTips(registerContext, sale, modalType).then(() => {
                                    defer.resolve();
                                });
                            } else {
                                defer.resolve();
                            }
                        } else {
                            defer.resolve();
                        }
                    })
                    .catch((response) => {
                        if (response.status === 409) { // conflict
                            this.modalService.alert(response.data.responseStatus.message);
                            defer.reject();
                            return;
                        }
                    })
                    .finally(() => {
                        this.busyService.stop();
                    });
            });

            return defer.promise;
        }

        private getLastSpecialPriceIndex(discountLines: Meevo.Model.DiscountLine[]): number {
            if (!discountLines || discountLines.length === 0) {
                return -1;
            }

            for (let index = discountLines.length - 1; index >= 0; index--) {
                let discount = discountLines[index];

                if (discount.isBuyItem && discount.isBuyGet) {
                    continue;
                }

                if (discount.type === Meevo.Model.DiscountTypeEnum.SpecialPrice) {
                    return index;
                }
            }

            return -1;
        }

        isSaleLineDiscounted(saleLine: Meevo.Model.SaleLine): boolean {
            // redeemed packages are considered "discounts"
            // http://pharmstfs01:8080/tfs/MillenniumSI/Meevo2/_workitems/edit/63465?fullScreen=false
            if (saleLine.packageRedeemed) {
                return true;
            }

            if (saleLine.discountLines && saleLine.discountLines.length > 0) {
                let specialPriceIndex = this.getLastSpecialPriceIndex(saleLine.discountLines);

                // if special price is the last discount, then it's not a discount
                if (specialPriceIndex === saleLine.discountLines.length - 1) {
                    return false;
                }

                return true;
            }

            return false;
        }

        isSaleDiscounted(sale: Meevo.Model.Sale): boolean {
            return _(sale.saleLines).any((saleLine: Meevo.Model.SaleLine) => this.isSaleLineDiscounted(saleLine));
        }

        calculateServicePrice(basePrice: number, finalPrice: number, useDiscountedPrice: boolean): number {
            return useDiscountedPrice
                ? finalPrice
                : basePrice;
        }

        getServiceTotal(registerContext: Meevo.Plugins.Register.RegisterContext, tipLines: Model.TipLine[], useDiscountedPrice: boolean): number {
            let serviceTotal = 0;
            _(tipLines).forEach((tipLine: RegisterTipLine) => {
                if (tipLine.employee) {
                    let total = this.calculateServicePrice(tipLine.baseTotal, tipLine.finalTotal, useDiscountedPrice);
                    serviceTotal = registerContext.addNormalizedNumbers(serviceTotal, total);
                }
            });
            return serviceTotal;
        }

        pruneMissingEmployees(sale: Model.Sale, houseEmployee: Model.EmployeeListItem) {
            _(sale.tipLines).forEach((tipLine: any) => {
                if (!tipLine.isServiceEmployee) {
                    return;
                }

                const existingEmployee = _(sale.saleLines)
                    .any(saleLine => saleLine.employeeId === tipLine.originalEmployeeId || saleLine.secondEmployeeId === tipLine.originalEmployeeId);

                if (!existingEmployee) {
                    tipLine.deleted = true;
                }
            });

            sale.tipLines = _(sale.tipLines).filter((tipLine: any) => !tipLine.deleted);

            if (sale.tipLines.length < 1) {
                 sale.tipConvenienceFee = 0;
            }
        }

        /**
         * Removes employees that were defaulted in from a sale line but have no tippable sale
         * line associated with them, so they do not show in the Tip Modal and are not sent to
         * MeevoTips.  Serves the same purpose as TipRulesEngine.PruneEmployeeLines on the server
         * and reaches the same tip totals, but the two are NOT line-for-line equivalent: the
         * House exemption is narrower here (see the isTipCatchAll guard below).
         *
         * Preserved (never pruned):
         *  - manually added employees (isServiceEmployee false)
         *  - refund tip lines
         *  - employees receiving a prepaid package tip
         *  - any tip line holding an externally collected tip (MeevoTips kiosk)
         *  - the House catch-all line distribution creates for an explicit tip amount
         *    (isTipCatchAll), which by definition has no tippable sale line
         *
         * Belongs before distributeTipIntoTipLines: that is where rebuildTipLines calls it and
         * where it reads most naturally.  It is safe after distribution too, because the House
         * catch-all is protected by isTipCatchAll rather than by call ordering - deleting that
         * line would silently discard the tip (defect #131476 code review).
         *
         * NOTE: tipLine.amount is deliberately NOT a guard.  distributeTipIntoTipLines zeroes and
         * re-derives amount on every service-employee line from sale.tipAmount/tipPercentage, so a
         * non-zero amount here is a stale value from the previous cycle, not a record of collected
         * money.  Guarding on it would leave a pruned-eligible employee visible for one full cycle
         * (long enough to reach the MeevoTips kiosk).  Externally collected tips carry
         * externalAmount/externalTipTransactionId, which distribution does not touch.
         */
        pruneNonTippableEmployees(sale: Model.Sale, tipLines: Model.TipLine[]) {
            if (!tipLines || tipLines.length < 1) {
                return;
            }

            // The sale lines, not tipLine.prepaidTipMinimum, are the authoritative source here.
            // rebuildTipLines calls calculatePrepaidTipMinimums first so the tip line would be
            // populated, but the Tip Modal path does not: loadTip goes straight to
            // generateTipEmployeeLines, and updateServiceTotals returns early on a non-tippable
            // sale line - which is exactly the line a prepaid package tip sits on.  Reading the
            // sale lines gives both callers the same answer.
            const hasPrepaidTip = (employeeId: string): boolean => {
                return _(sale.saleLines).any((saleLine: Model.SaleLine) =>
                    (saleLine.prepaidTipRedeemed || 0) > 0 &&
                    (saleLine.employeeId === employeeId || saleLine.secondEmployeeId === employeeId));
            };

            // externalAmount lives on Model.TipLine; hasTippableSaleLine and isTipCatchAll live on
            // RegisterTipLine, which implements Model.TipLine without redeclaring externalAmount.
            // The intersection is what this function actually reads, so the single cast sits at the
            // loop boundary below rather than switching the compiler off inside the guard itself.
            type PrunableTipLine = Model.TipLine & RegisterTipLine;

            const shouldPrune = (tipLine: PrunableTipLine): boolean => {
                // manually added employees and refunds are always kept
                if (!tipLine.isServiceEmployee || tipLine.isRefund) {
                    return false;
                }

                // employee has at least one tippable sale line
                if (tipLine.hasTippableSaleLine) {
                    return false;
                }

                // the House catch-all holding an explicit tip amount is never a candidate.
                //
                // Dormant guard: rebuildTipLines clears isTipCatchAll before this runs, and prune
                // never runs after distribution, so nothing reaches here with the flag set today.
                // It is kept so the prune-before-distribute ordering lives in the data rather than
                // in the call order - see addHouseEmployeeTipLine for why that matters.
                //
                // Deliberately NARROWER than the server's exemption, not a mirror of it.
                // PruneEmployeeLines keeps every line with OriginalEmployeeId == Guid.Empty; this
                // keeps only the line distribution created to hold a tip.  So a House line
                // defaulted in from a sale line performed by House is pruned here but survives
                // there.  Both paths still land on the same tip: an explicit amount re-creates
                // this line in distribution, and a percentage tip is $0 either way, because the
                // server's surviving line carries TippableServiceTotal 0.
                if (tipLine.isTipCatchAll) {
                    return false;
                }

                // never drop an externally collected tip (MeevoTips kiosk)
                if (tipLine.externalAmount || tipLine.externalTipTransactionId) {
                    return false;
                }

                // prepaid package tips still apply to a non-tippable service
                return !hasPrepaidTip(tipLine.originalEmployeeId);
            };

            for (let index = tipLines.length - 1; index >= 0; index--) {
                if (shouldPrune(<PrunableTipLine>tipLines[index])) {
                    tipLines.splice(index, 1);
                }
            }
        }

        distributeTipIntoTipLines(registerContext: Meevo.Plugins.Register.RegisterContext, tipLines: Model.TipLine[], tipAmount: number, externalTipAmount: number = 0) {
            _(tipLines).each((tipLine: RegisterTipLine) => {
                if (tipLine.isServiceEmployee) {
                    tipLine.amount = 0;
                }
            });

            const applicableTipAmount = registerContext.normalizeDecimalPlaces(tipAmount - externalTipAmount);
            let tipRemainingAmount = this._distributeTipIntoTipLines(registerContext, tipLines, applicableTipAmount);

			// prepaid tip minimum can throw off proportional distribution and cause an overage
			// remove the overage proportionately from employees without prepaid tip minimum

            if (tipRemainingAmount !== 0) {
                tipRemainingAmount = this._distributeTipIntoTipLines(registerContext, tipLines, tipRemainingAmount);
            }

            // if there is still any remainder left (loose pennies) give it to first eligible tip employee

            if (tipRemainingAmount !== 0 && tipLines) {
                let tipEmployeeLine = tipLines.find(tipLine => {
                    const amount = registerContext.subtractNormalizedNumbers(tipLine.amount, tipLine.prepaidTipMinimum);
                    return registerContext.addNormalizedNumbers(amount, tipRemainingAmount) >= 0;
                });

                if (!tipEmployeeLine) {
                    // catch-all for the loose pennies: mark it so a prune pass cannot drop them
                    tipEmployeeLine = this.addHouseEmployeeTipLine(registerContext, tipLines, true, true);
                }

                if (tipEmployeeLine.isServiceEmployee) {
                    tipEmployeeLine.amount = registerContext.addNormalizedNumbers(tipEmployeeLine.amount, tipRemainingAmount);
                }

                tipRemainingAmount = 0;
            }
        }

        private _distributeTipIntoTipLines(registerContext: Meevo.Plugins.Register.RegisterContext, tipLines: Model.TipLine[], tipAmount: number) {
            console.log("_distributeTipIntoTipLines");

            if (tipAmount > 0 && tipLines.length === 0) {
                // every employee was pruned, so House holds the explicit tip amount.  Marked as
                // the catch-all so a later prune pass cannot delete the line and lose the tip.
                this.addHouseEmployeeTipLine(registerContext, tipLines, true, true);
            }

            let tipRemainingAmount = tipAmount;

            const isEligibleTipLine = (tipLine: any) => {
                return tipLine.isServiceEmployee &&
                    (
                        !tipLine.prepaidTipMinimum ||
                        (tipAmount > 0 && tipLine.amount < tipLine.prepaidTipMinimum) ||
                        (tipAmount < 0 && tipLine.amount > tipLine.prepaidTipMinimum)
                    ) &&
                    (
                        (tipLine.employee && tipLine.employee.allowTips) ||
                        tipLine.originalEmployeeId == registerContext.HouseEmployee.id ||
                        tipLine.serviceTotal > 0 ||
                        tipLine.prepaidTipMinimum > 0
                    );
            };

            let saleServiceTotal = 0;
            let eligibleTipLinesCount = 0;

            _(tipLines).each((tipLine: any) => {
                if (isEligibleTipLine(tipLine)) {
                    saleServiceTotal += tipLine.serviceTotal;
                    eligibleTipLinesCount++;
                }
            });

            saleServiceTotal = registerContext.normalizeDecimalPlaces(saleServiceTotal);

            for (const tipLine of tipLines) {
                if (!isEligibleTipLine(tipLine)) {
                    continue;
                }

                let adjustment = 0;

                if (saleServiceTotal === 0) {
                    adjustment = tipAmount / eligibleTipLinesCount;
                } else {
                    const ratio = (<any>tipLine).serviceTotal / saleServiceTotal;
                    adjustment = tipAmount * ratio;
                }

                if (tipLine.amount + adjustment < tipLine.prepaidTipMinimum || 0) {
                    adjustment = tipLine.prepaidTipMinimum - tipLine.amount;
                }

                tipLine.amount = registerContext.addNormalizedNumbers(tipLine.amount, adjustment);
                tipRemainingAmount = registerContext.subtractNormalizedNumbers(tipRemainingAmount, adjustment);
                tipLine.percentage = null;
            }

            return tipRemainingAmount;
        }

        getTipLine(
            registerContext: Meevo.Plugins.Register.RegisterContext,
            employeeId: string,
            employee: Model.EmployeeListItem,
            originalEmployeeId: string,
            originalEmployee: Model.EmployeeListItem,
            amount: number = 0,
            percentage: number = null,
            baseTotal: number = 0,
            finalTotal: number = 0,
            isServiceEmployee: boolean = false
        ): RegisterTipLine {
            const tipLine = {
                employeeId: employeeId,
                employee: employee,
                originalEmployeeId: originalEmployeeId,
                originalEmployee: originalEmployee,
                amount: amount,
                percentage: percentage,
                forcedDisplayValue: percentage == null ? null : registerContext.normalizeDecimalPlaces(percentage * 100),
                baseTotal: baseTotal,
                finalTotal: finalTotal,
                serviceTotal: 0,
                tippableServiceTotal: 0,
                isServiceEmployee: isServiceEmployee,
                prepaidTipMinimum: 0,
                isRefund: false,
                hasTippableSaleLine: false
            };

            return tipLine;
        }

        addTipLine(
            registerContext: Meevo.Plugins.Register.RegisterContext,
            tipLines: Model.TipLine[],
            employeeId: string,
            employee: Model.EmployeeListItem,
            originalEmployeeId: string,
            originalEmployee: Model.EmployeeListItem,
            amount: number = 0,
            percentage: number = null,
            baseTotal: number = 0,
            finalTotal: number = 0,
            isServiceEmployee: boolean = false
        ): RegisterTipLine {
            const tipLine = this.getTipLine(
                registerContext,
                employeeId,
                employee,
                originalEmployeeId,
                originalEmployee,
                amount,
                percentage,
                baseTotal,
                finalTotal,
                isServiceEmployee
            );

            tipLines.push(tipLine);

            return tipLine;
        }

        generateTipEmployeeLines(registerContext: Meevo.Plugins.Register.RegisterContext, sale: Model.Sale, tipLines: Model.TipLine[], includeProductsInTips: boolean) {
            const addTipLine = (
                employeeId: string,
                employee: Model.EmployeeListItem,
                originalEmployeeId: string,
                originalEmployee: Model.EmployeeListItem,
                isServiceEmployee: boolean = false
            ): RegisterTipLine => {
                return this.addTipLine(
                    registerContext,
                    tipLines || sale.tipLines,
                    employeeId || employee.id,
                    employee,
                    originalEmployeeId || originalEmployee.id,
                    originalEmployee,
                    0,
                    null,
                    0,
                    0,
                    isServiceEmployee
                );
            }

            const updateTipLine = (
                employee: Model.EmployeeListItem,
                employeeId: string,
                saleLine: Model.SaleLine,
                saleLineEmployee: Model.EmployeeListItem,
                saleLineEmployeeId: string
            ) => {
                let existingTipLine = _(tipLines || sale.tipLines).findWhere({ employeeId: employeeId, originalEmployeeId: saleLineEmployeeId, isRefund: false });

                if (!existingTipLine) {
                    existingTipLine = addTipLine(employeeId, employee, saleLineEmployeeId, saleLineEmployee, true);
                }

                if (tipLines) {
                    let saleTipLine = _(sale.tipLines).findWhere({ employeeId: employeeId, originalEmployeeId: saleLineEmployeeId, isRefund: false });

                    if (saleTipLine) {
                        existingTipLine.amount = saleTipLine.amount;
                        existingTipLine.percentage = saleTipLine.percentage;
                    }
                }

                this.updateServiceTotals(registerContext, sale, existingTipLine, saleLine, saleLineEmployeeId, includeProductsInTips);
            };

            const nonTippableClientPackageServicesUsedIds = this.getNonTippableClientPackageServicesUsedIds(sale.saleLines);

            for (const saleLine of sale.saleLines) {
                if (!this.isSaleLineEligibleForTip(saleLine, nonTippableClientPackageServicesUsedIds, includeProductsInTips)) {
                    continue;
                }

                const houseEmployee = registerContext.HouseEmployee;

                // NOTE: a House tip line is no longer created up-front for a non-tippable service
                // sold by non-tippable employee(s).  Employees with no tippable sale line are pruned
                // by pruneNonTippableEmployees; if the user enters an explicit tip amount with no
                // eligible employees, _distributeTipIntoTipLines still falls back to House.

                if (saleLine.employeeId) {
                    const employee   = saleLine.employee.allowTips ? saleLine.employee   : houseEmployee;
                    const employeeId = saleLine.employee.allowTips ? saleLine.employeeId : houseEmployee.id;
                    updateTipLine(employee, employeeId, saleLine, saleLine.employee, saleLine.employeeId);
                }

                if (saleLine.secondEmployeeId) {
                    const employee   = saleLine.secondEmployee.allowTips ? saleLine.secondEmployee   : houseEmployee;
                    const employeeId = saleLine.secondEmployee.allowTips ? saleLine.secondEmployeeId : houseEmployee.id;
                    updateTipLine(employee, employeeId, saleLine, saleLine.secondEmployee, saleLine.secondEmployeeId);
                }
            }

            this.restoreManuallyAddedEmployees(sale, tipLines);
        }

        /**
         * Only relevant when generateTipEmployeeLines is filling a separate array - the Tip Modal
         * builds a fresh one on every open, so isServiceEmployee is derived from the sale lines
         * rather than read from the tip line the user saved.
         *
         * For an employee the user manually added (saved with isServiceEmployee false) who is on a
         * sale line but has no tippable one, that derivation flips her to a service employee and
         * pruneNonTippableEmployees then drops her, silently undoing the manual add - along with
         * any amount on it.  Restore the saved flag for exactly those lines.
         *
         * An employee who has since picked up a tippable sale line is deliberately left as a
         * service employee: she is no longer at risk of being pruned, and isServiceEmployee also
         * decides who takes part in tip distribution, so forcing her back to a manual line would
         * freeze her amount instead of letting it re-derive.
         */
        private restoreManuallyAddedEmployees(sale: Model.Sale, tipLines: Model.TipLine[]) {
            if (!tipLines) {
                return;
            }

            _(tipLines).each((tipLine: RegisterTipLine) => {
                if (!tipLine.isServiceEmployee || tipLine.hasTippableSaleLine) {
                    return;
                }

                const saleTipLine = <RegisterTipLine>_(sale.tipLines).findWhere({
                    employeeId: tipLine.employeeId,
                    originalEmployeeId: tipLine.originalEmployeeId,
                    isRefund: false
                });

                // false only ever comes from a manual add; undefined is a legacy line and stays
                // on the derived value
                if (saleTipLine && saleTipLine.isServiceEmployee === false) {
                    tipLine.isServiceEmployee = false;
                }
            });
        }

        private isSaleLineEligibleForTip(currentSaleLine: Model.SaleLine, nonTippableClientPackageServicesUsedIds: string[], includeProductsInTips: boolean) {
            // TipLogic.cs IsSaleLineEligibleForTip has similar logic for tip modal tipping.
            // NOTE: intentionally differs from the server version, which also returns false for a
            // service flagged doNotAllowTips.  Here a non-tippable service stays eligible so that
            // updateServiceTotals/calculatePrepaidTipMinimums still see it (prepaid package tips
            // must keep applying).  Employees left with no tippable sale line are removed later by
            // pruneNonTippableEmployees.

            if (currentSaleLine.isRefund) {
                return false;
            }

            switch (currentSaleLine.saleType) {
                case Model.SalesTypeEnum.Service:
                    return true;

                case Model.SalesTypeEnum.Product:
                    return includeProductsInTips;

                case Model.SalesTypeEnum.PackageServiceSwapFee:
                    const swapFeeClientPackageServiceItem = currentSaleLine.item as Model.PackageServiceSwapFeeSaleLineItem;

                    if (!swapFeeClientPackageServiceItem || !swapFeeClientPackageServiceItem.id) {
                        return false;
                    }

                    return !_(nonTippableClientPackageServicesUsedIds).contains(swapFeeClientPackageServiceItem.clientPackageServiceId);

                default:
                    return false;
            }
        }

        private getNonTippableClientPackageServicesUsedIds(saleLines: Model.SaleLine[]) {
            return _(saleLines).chain()
                .filter((saleLine: Model.SaleLine) => saleLine.saleType === Model.SalesTypeEnum.Service &&
                    saleLine.packageRedemptionPath && (saleLine.item as ServiceSaleLineItem).doNotAllowTips)
                .map(serviceSaleLineWithRedemptionPath =>
                    serviceSaleLineWithRedemptionPath.packageRedemptionPath.clientPackageServicesUsed)
                .flatten()
                .map(clientPackageServiceUsed => clientPackageServiceUsed.packageService.id)
                .value();
        }

        refreshTips(registerContext: Meevo.Plugins.Register.RegisterContext, tipLines: Model.TipLine[]): ng.IPromise<number> {
            return this.dataService.add("saletip", { tipLines }).then((response) => {
                let currentTip = 0;

                for (let index = 0; index < response.tipLines.length; index++) {
                    const tipLine = tipLines[index] as RegisterTipLine;
                    const responseTipLine = response.tipLines[index] as RegisterTipLine;
                    tipLine.amount = responseTipLine.amount;
                    tipLine.percentage = responseTipLine.percentage;
                    tipLine.forcedDisplayValue = registerContext.normalizeDecimalPlaces(tipLine.percentage * 100);

                    currentTip = registerContext.addNormalizedNumbers(currentTip, tipLine.amount);
                }

                return currentTip;
            });
        }

        rebuildTipLines(registerContext: Meevo.Plugins.Register.RegisterContext, sale: Model.Sale) {
            _(sale.tipLines).forEach((tipLine: any) => {
                tipLine.baseTotal = 0;
                tipLine.finalTotal = 0;
                // recalculated by updateServiceTotals below; must be reset so it cannot go stale
                // when a sale line or the product-tipping preference changes
                tipLine.hasTippableSaleLine = false;
                // re-earned by distributeTipIntoTipLines only while a tip amount still needs a
                // home.  Load-bearing, not hygiene: without it a House catch-all left over from a
                // tip the user has since cleared carries its exemption into this cycle, survives
                // pruneNonTippableEmployees and keeps the stale tip on the ticket.  This reset is
                // also why that exemption never fires on any current path - see
                // addHouseEmployeeTipLine.
                tipLine.isTipCatchAll = false;
            });

            this.generateTipEmployeeLines(registerContext, sale, null, registerContext.includeProductsInTips);
            this.calculatePrepaidTipMinimums(registerContext, sale.tipLines, sale.saleLines);
            this.pruneMissingEmployees(sale, registerContext.HouseEmployee);
            this.pruneNonTippableEmployees(sale, sale.tipLines);
        }

        getSaleLineTipAmount(registerContext: Meevo.Plugins.Register.RegisterContext, tipPercentage: number, tipLines: Model.TipLine[], useDiscountedPrice: boolean): number {
            let tipAmount = 0;
            for (const tipLine of tipLines) {
                const serviceTotal = this.calculateServicePrice(tipLine.baseTotal, tipLine.finalTotal, useDiscountedPrice);
                const additionalTipAmount = Math.max(tipLine.prepaidTipMinimum, registerContext.normalizeDecimalPlaces(serviceTotal * tipPercentage));
                tipAmount = registerContext.addNormalizedNumbers(tipAmount, additionalTipAmount);
            }
            return tipAmount;
        }

        private getTipAmount(registerContext: Meevo.Plugins.Register.RegisterContext, sale: Model.Sale): number {
            let tipAmount = 0;
            if (sale.tipAmount) {
                tipAmount = Math.max(sale.totalPrepaidTipRedeemed, sale.tipAmount);
            } else if (sale.tipPercentage) {
                tipAmount = this.getSaleLineTipAmount(registerContext, sale.tipPercentage, sale.tipLines, sale.useDiscountedPrice);
            } else {
                tipAmount = sale.totalPrepaidTipRedeemed;
            }
            return tipAmount;
        }

        recalculateTip(registerContext: Meevo.Plugins.Register.RegisterContext, sale: Model.Sale): ng.IPromise<any> {
            const deferred = this.$q.defer();

            sale.recalculateTipInProgress = true;

            this.rebuildTipLines(registerContext, sale);

            const isAutomaticMode = sale.tipAmount > 0 || sale.tipPercentage > 0;
            const isPrepaidTipNotDistributed =
                sale.totalPrepaidTipRedeemed > 0 &&
                sale.tipLines.length === 0 ||
                (
                    sale.tipLines.length > 0 &&
                    (
                        _(sale.tipLines).any(tipLine => tipLine.amount < tipLine.prepaidTipMinimum) ||
                        _(sale.tipLines).all(tipLine => !tipLine.prepaidTipMinimum)
                    )
                );

            if (!isAutomaticMode && !isPrepaidTipNotDistributed) {
                this.updateTip(registerContext, sale);
                sale.recalculateTipInProgress = false;
                deferred.resolve();
                return deferred.promise;
            }

            if (isAutomaticMode && sale.tipPercentage > 0) {
                sale.tipAmount = null;
            }

            let tipAmount = this.getTipAmount(registerContext, sale);

            if (isAutomaticMode || isPrepaidTipNotDistributed) {
                _(sale.tipLines).forEach((tipLine: any) => {
                    tipLine.serviceTotal = this.calculateServicePrice(tipLine.baseTotal, tipLine.finalTotal, sale.useDiscountedPrice);
                });
            }

            if (tipAmount > 0) {
                this.distributeTipIntoTipLines(registerContext, sale.tipLines, tipAmount);
            }

            // NOTE: no second prune pass here, and none is needed.  Nothing distribution touches
            // is a prune guard - hasTippableSaleLine, isServiceEmployee, isRefund, externalAmount
            // and the sale lines behind the prepaid check are all left alone - so a second pass
            // cannot find a newly prune-eligible employee.  Pruning belongs in rebuildTipLines,
            // before distribution.  (A pass here would no longer lose the tip: the House catch-all
            // carries isTipCatchAll and is exempt.  It would simply be dead work.)
            let saleTipLines = _(sale.tipLines).filter(tipLine => {
                return !tipLine.isRefund;
            });
            this.refreshTips(registerContext, saleTipLines).then((currentTip: number) => {
                const lastTipLine = _(saleTipLines).last();

                if (lastTipLine && sale.tipAmount) {
                    const roundingError = registerContext.normalizeDecimalPlaces(sale.tipAmount - currentTip);
                    if (roundingError !== 0) {
                        lastTipLine.amount = registerContext.addNormalizedNumbers(lastTipLine.amount, roundingError);
                        currentTip = sale.tipAmount;
                    }
                }

                this.updateTip(registerContext, sale);

                deferred.resolve();
            }).finally(() => {
                sale.recalculateTipInProgress = false;
            });

            return deferred.promise;
        }

        private calculateSpecialPriceCharge(registerContext: Meevo.Plugins.Register.RegisterContext, amount: number, saleLine: Model.SaleLine, lastSpecialPriceIndex: number) {
            // if there are no discounts after special price, then just use finalCharge
            if (saleLine.discountLines.length - 1 === lastSpecialPriceIndex) {
                return saleLine.finalCharge;
            }

            // unfortunately no charge amount exists for a given special price, so it needs to be recalculated
            let itemCharge = 0;

            for (const saleLineCharge of saleLine.charges) {
                itemCharge += saleLineCharge.isPercent
                    ? registerContext.normalizeDecimalPlaces(amount * saleLineCharge.chargeAmount)
                    : saleLineCharge.chargeAmount;
            }

            return registerContext.normalizeDecimalPlaces(itemCharge * saleLine.quantity);
        }

        private getSalePercent(saleLine: Model.SaleLine, employee: Model.EmployeeListItem, employeeId: string, otherEmployee: Model.EmployeeListItem, otherEmployeeId: string, houseEmployeeId: string): number {
            let salePercent = 1;

            if (employee && !otherEmployee) {
                salePercent = 1;
            }
            else if (otherEmployee && !employee) {
                salePercent = 1;
            }
            else if (employee && otherEmployee && employee.allowTips && !otherEmployee.allowTips) {
                salePercent = 1;
            }
            else if (employee && otherEmployee && !employee.allowTips && otherEmployee.allowTips) {
                salePercent = 0;
            }
            else {
                const fullCommission = saleLine.employeeSplitCommission === 1 && saleLine.secondEmployeeSplitCommission === 1;

                if (fullCommission) {
                    salePercent = 0.5;
                } else {
                    let splitCommission = 0;

                    switch (employeeId) {
                        case saleLine.employeeId:
                            splitCommission = saleLine.employeeSplitCommission;
                            break;
                        case saleLine.secondEmployeeId:
                            splitCommission = saleLine.secondEmployeeSplitCommission;
                            break;
                    }

                    salePercent = splitCommission;
                }
            }

            return salePercent;
        }

        private getDiscountOffset(registerContext: Meevo.Plugins.Register.RegisterContext, discountLines: Model.DiscountLine[]) {
            let discountOffset = {
                memberBenefitDiscount: 0,
                yieldIncrease: 0,
            };

            if (!discountLines) {
                return discountOffset;
            }
            let lastSpecialPriceIndex = this.getLastSpecialPriceIndex(discountLines);

            for (let index = lastSpecialPriceIndex + 1; index < discountLines.length; index++) {
                const discountLine = discountLines[index];

                if (discountLine.type === Meevo.Model.DiscountTypeEnum.SpecialPrice) {
                    continue;
                }

                const discountAmount = -discountLine.amount;

                switch (discountLine.saleLineDiscountType) {
                    case Meevo.Model.SaleLineDiscountTypeEnum.MembershipBenefit:
                        discountOffset.memberBenefitDiscount = registerContext.addNormalizedNumbers(discountOffset.memberBenefitDiscount, discountAmount);
                        break;

                    case Meevo.Model.SaleLineDiscountTypeEnum.YieldManagement:
                        if (discountAmount > 0) {
                            discountOffset.yieldIncrease = registerContext.addNormalizedNumbers(discountOffset.yieldIncrease, discountAmount);
                        }
                        break;
                }
            }

            return discountOffset;
        }

        private getBasePrice(registerContext: Meevo.Plugins.Register.RegisterContext, saleLine: Model.SaleLine): number {
            const lastSpecialPriceIndex = this.getLastSpecialPriceIndex(saleLine.discountLines);
            let basePrice = 0;
            if (lastSpecialPriceIndex !== -1) {
                const lastSpecialPriceDiscount = saleLine.discountLines[lastSpecialPriceIndex];
                const baseItemPrice = lastSpecialPriceDiscount.discountValue || 0;
                basePrice = registerContext.normalizeDecimalPlaces(baseItemPrice * saleLine.quantity);

                const baseCharge = this.calculateSpecialPriceCharge(registerContext, baseItemPrice, saleLine, lastSpecialPriceIndex);

                basePrice = registerContext.addNormalizedNumbers(basePrice, baseCharge);
            } else {
                basePrice = saleLine.basePriceCharge;
            }

            const discountOffset = this.getDiscountOffset(registerContext, saleLine.discountLines);

            // if yield management increase exists then basePrice is adjusted by member benefit discount and yield management increase

            if (discountOffset.yieldIncrease > 0) {
                const basePriceAdjustment = discountOffset.memberBenefitDiscount + discountOffset.yieldIncrease;

                basePrice = registerContext.addNormalizedNumbers(basePrice, basePriceAdjustment);
            }

            return basePrice;
        }

        updateServiceTotals(registerContext: Meevo.Plugins.Register.RegisterContext, sale: Model.Sale, tipLine: Model.TipLine, saleLine: Model.SaleLine, employeeId: string, includeProductsInTips: boolean) {
            const ignoreTips =
                (saleLine.saleType === Meevo.Model.SalesTypeEnum.Service && saleLine.item.doNotAllowTips) ||
                (saleLine.saleType === Meevo.Model.SalesTypeEnum.Product && !includeProductsInTips);

            if (ignoreTips) {
                return;
            }

            const houseEmployee = registerContext.HouseEmployee;
            const houseEmployeeId = houseEmployee.id;

            let employee: Model.EmployeeListItem;
            let otherEmployee: Model.EmployeeListItem;
            let otherEmployeeId: string;

            switch (employeeId) {
                case saleLine.employeeId:
                    employee = saleLine.employee;
                    if (employee) {
                        otherEmployee = saleLine.secondEmployee;
                        otherEmployeeId = saleLine.secondEmployeeId || (saleLine.secondEmployee ? saleLine.secondEmployee.id : null) || houseEmployeeId;
                    }
                    break;

                case saleLine.secondEmployeeId:
                    employee = saleLine.secondEmployee;
                    if (employee) {
                        otherEmployee = saleLine.employee;
                        otherEmployeeId = saleLine.employeeId || (saleLine.employee ? saleLine.employee.id : null) || houseEmployeeId;
                    }
                    break;
            }

            let originalEmployee: Model.EmployeeListItem;
            let originalEmployeeId: string;
            let originalOtherEmployee: Model.EmployeeListItem;
            let originalOtherEmployeeId: string;

            if (employee) {
                originalEmployee = employee;
                originalEmployeeId = employeeId;
                if (!employee.allowTips) {
                    employee = houseEmployee;
                    employeeId = houseEmployeeId;
                }
            }
            if (otherEmployee) {
                originalOtherEmployee = otherEmployee;
                originalOtherEmployeeId = otherEmployeeId;
                if (!otherEmployee.allowTips) {
                    otherEmployee = houseEmployee;
                    otherEmployeeId = houseEmployeeId;
                }
            }

            if (!tipLine) {
                tipLine = {
                    employeeId: employeeId,
                    employee: employee,
                    originalEmployeeId: originalEmployeeId,
                    originalEmployee: originalEmployee,
                    amount: 0,
                    percentage: null,
                    forcedDisplayValue: 0,
                    baseTotal: 0,
                    finalTotal: 0,
                    serviceTotal: 0,
                    isServiceEmployee: true,
                    isRefund: false
                } as RegisterTipLine;

                sale.tipLines.push(tipLine);
            }

            const salePercent = this.getSalePercent(
                saleLine,
                originalEmployee,
                originalEmployeeId,
                originalOtherEmployee,
                originalOtherEmployeeId,
                houseEmployeeId);

            const basePriceCharge = this.getBasePrice(registerContext, saleLine);
            const finalPriceCharge = saleLine.finalPriceCharge;
            const prepaidTipRedeemed = saleLine.prepaidTipRedeemed || 0;

            let splitBasePriceCharge;
            let splitFinalPriceCharge;
            let splitPrepaidTipRedeemed;

            // for second employee, use remaining amount instead of calculating by second employee's sale percent
            if (saleLine.secondEmployeeId == employeeId && salePercent > 0 && salePercent < 1) {
                const otherSalePercent = 1 - salePercent;
                const otherSplitBasePriceCharge = registerContext.normalizeDecimalPlaces(basePriceCharge * otherSalePercent);
                const otherSplitFinalPriceCharge = registerContext.normalizeDecimalPlaces(finalPriceCharge * otherSalePercent);
                const otherSplitPrepaidTipRedeemed = registerContext.normalizeDecimalPlaces(prepaidTipRedeemed * otherSalePercent);

                splitBasePriceCharge = registerContext.normalizeDecimalPlaces(basePriceCharge - otherSplitBasePriceCharge);
                splitFinalPriceCharge = registerContext.normalizeDecimalPlaces(finalPriceCharge - otherSplitFinalPriceCharge);
                splitPrepaidTipRedeemed = registerContext.normalizeDecimalPlaces(prepaidTipRedeemed - otherSplitPrepaidTipRedeemed);
            } else {
                splitBasePriceCharge = registerContext.normalizeDecimalPlaces(basePriceCharge * salePercent);
                splitFinalPriceCharge = registerContext.normalizeDecimalPlaces(finalPriceCharge * salePercent);
                splitPrepaidTipRedeemed = registerContext.normalizeDecimalPlaces(prepaidTipRedeemed * salePercent);
            }

            // the ignoreTips check above means we only get here for tippable sale lines,
            // so this marks the employee as eligible to default into the Tip Modal / MeevoTips
            (<RegisterTipLine>tipLine).hasTippableSaleLine = true;

            tipLine.baseTotal = registerContext.addNormalizedNumbers(tipLine.baseTotal, splitBasePriceCharge);
            tipLine.finalTotal = registerContext.addNormalizedNumbers(tipLine.finalTotal, splitFinalPriceCharge);
            tipLine.prepaidTipMinimum = registerContext.addNormalizedNumbers(tipLine.prepaidTipMinimum, splitPrepaidTipRedeemed);
        }

        updateTip(registerContext: Meevo.Plugins.Register.RegisterContext, sale: Model.Sale, tipLines: any[] = null, tipPercentage: number = null, tipAmount: number = null, useDiscountedPrice: boolean = null) {
            if (!sale) {
                return;
            }

            if (tipLines != null) {
                sale.tipLines = angular.copy(tipLines);
                sale.tipPercentage = tipPercentage;
                sale.tipAmount = tipAmount;
                sale.useDiscountedPrice = useDiscountedPrice;
            }

            let currentTip = 0;
            let currentTipRefund = 0;
            for (const tipLine of sale.tipLines) {
                if (tipLine.isRefund) {
                    currentTipRefund += tipLine.amount;
                } else {                     
                    currentTip += tipLine.amount;
                }
            }

            const totalPrepaidTipRedeemed = sale.totalPrepaidTipRedeemed || 0;

            currentTip = currentTip <= 0
                ? currentTip + totalPrepaidTipRedeemed
                : Math.max(0, currentTip - totalPrepaidTipRedeemed) + totalPrepaidTipRedeemed;

            sale.tip = registerContext.normalizeDecimalPlaces(currentTip);
            sale.tipRefund = registerContext.normalizeDecimalPlaces(currentTipRefund);
            registerContext.updateTotal(sale);

            sale.deviceTipBypass = (sale.tip + sale.tipRefund) > 0
                ? true
                : this.getTipMode(sale.tipAmount, sale.tipPercentage) !== TipMode.Unset;

            registerContext.recalculateChangeDue(sale);
        }

        private calculatePrepaidTipMinimum(
            registerContext: Meevo.Plugins.Register.RegisterContext,
            tipLines: Model.TipLine[],
            prepaidTipRedeemed: number,
            employeeId: string,
            employeeSplitCommission: number,
            otherEmployeeId: string,
            otherEmployeeSplitCommission: number,
            isPrimaryEmployee: boolean
        ) {
            if (!prepaidTipRedeemed) {
                return;
            }

            const existingTipLine = _(tipLines).find(tipLine => tipLine.originalEmployeeId === employeeId &&
                !tipLine.isRefund);

            if (!existingTipLine) {
                return;
            }

            const fullCommission = (employeeSplitCommission === 1 && otherEmployeeSplitCommission === 1) ? 0.5 : null;

            const existingAllowTips = existingTipLine.originalEmployee.allowTips === true;
            const existingOtherTipLine = _(tipLines).find(tipLine => tipLine.originalEmployeeId === otherEmployeeId &&
                !tipLine.isRefund);
            const existingOtherAllowTips = existingOtherTipLine && existingOtherTipLine.originalEmployee.allowTips === true;
            const neitherAllowTips = !existingAllowTips && (!existingOtherTipLine || !existingOtherAllowTips);

            const tipSplitPercentage = fullCommission || (employeeSplitCommission != undefined ? employeeSplitCommission : 1);
            const prepaidTipMinimum = isPrimaryEmployee
                ? registerContext.normalizeDecimalPlaces(prepaidTipRedeemed * tipSplitPercentage)
                : registerContext.normalizeDecimalPlaces(prepaidTipRedeemed - registerContext.normalizeDecimalPlaces(prepaidTipRedeemed * (1 - tipSplitPercentage)));

            if (existingAllowTips || neitherAllowTips) {
                existingTipLine.prepaidTipMinimum = registerContext.addNormalizedNumbers(existingTipLine.prepaidTipMinimum || 0, prepaidTipMinimum);
            }
            else if (existingOtherAllowTips) {
                existingOtherTipLine.prepaidTipMinimum = registerContext.addNormalizedNumbers(existingOtherTipLine.prepaidTipMinimum || 0, prepaidTipMinimum);
            }
        }

        calculatePrepaidTipMinimums(registerContext: Meevo.Plugins.Register.RegisterContext, tipLines: Model.TipLine[], saleLines: Model.SaleLine[]) {
            for (const tipLine of tipLines.filter(tipLine => !tipLine.isRefund)) {
                tipLine.prepaidTipMinimum = 0;
            }

            for (const saleLine of saleLines) {
                if (saleLine.employeeId) {
                    this.calculatePrepaidTipMinimum(
                        registerContext,
                        tipLines,
                        saleLine.prepaidTipRedeemed,
                        saleLine.employeeId,
                        saleLine.employeeSplitCommission,
                        saleLine.secondEmployeeId,
                        saleLine.secondEmployeeSplitCommission,
                        true);
                }

                if (saleLine.secondEmployeeId) {
                    this.calculatePrepaidTipMinimum(
                        registerContext,
                        tipLines,
                        saleLine.prepaidTipRedeemed,
                        saleLine.secondEmployeeId,
                        saleLine.secondEmployeeSplitCommission,
                        saleLine.employeeId,
                        saleLine.employeeSplitCommission,
                        false);
                }
            }
        }

        private getHouseEmployeeTipLine(registerContext: Meevo.Plugins.Register.RegisterContext, tipLines: Model.TipLine[]): Model.TipLine {
            return _(tipLines).find(tipLine =>
                tipLine.employeeId === registerContext.HouseEmployee.id &&
                tipLine.originalEmployeeId === registerContext.HouseEmployee.id);
        }

        /**
         * Get-or-create: returns the existing House line if one is already present, otherwise
         * appends a new one.  Both callers pass isServiceEmployee/isTipCatchAll true, so the
         * defaults are currently unreachable.
         *
         * isTipCatchAll marks a line created to hold a distributed tip with nowhere else to go,
         * exempting it from pruneNonTippableEmployees.  It is applied only on the create path -
         * an existing line is returned untouched.  That is safe rather than lucky: any House line
         * that can already be present here is exempt from pruning on another guard (a tippable
         * sale line sets hasTippableSaleLine, a manual add clears isServiceEmployee, and a
         * non-tippable one was pruned before distribution ran), so no caller can receive a
         * prune-eligible line.
         *
         * NOTE: the guard fires on no current code path, because prune only ever runs before
         * distribution and rebuildTipLines resets isTipCatchAll immediately beforehand.  The reset
         * is doing real work, though - drop it and a catch-all left over from a tip the user has
         * since cleared keeps the flag into the next cycle, the guard exempts it, and the stale
         * tip survives on the ticket.  The guard itself is insurance: it puts the
         * prune-before-distribute ordering in the data rather than in the call order, so re-adding
         * a prune pass after distribution would find the flag set and leave the tip alone.
         * Losing that tip is defect #131476's code review finding, so keep both.
         *
         * NOTE: this object is a hand-rolled duplicate of getTipLine and has to stay in step with
         * it.  Not consolidated here only because getTipLine derives forcedDisplayValue from
         * percentage (null rather than 0), which is a separate behaviour change.
         */
        addHouseEmployeeTipLine(
            registerContext: Meevo.Plugins.Register.RegisterContext,
            tipLines: Model.TipLine[],
            isServiceEmployee: boolean = false,
            isTipCatchAll: boolean = false
        ) {
            const houseEmployee = this.getHouseEmployeeTipLine(registerContext, tipLines);
            if (houseEmployee) {
                return houseEmployee;
            }

            const tipLine = {
                employeeId: registerContext.HouseEmployee.id,
                employee: registerContext.HouseEmployee,
                originalEmployeeId: registerContext.HouseEmployee.id,
                originalEmployee: registerContext.HouseEmployee,
                amount: 0,
                percentage: null,
                forcedDisplayValue: 0,
                baseTotal: 0,
                finalTotal: 0,
                serviceTotal: 0,
                tippableServiceTotal: 0,
                isServiceEmployee: isServiceEmployee,
                prepaidTipMinimum: 0,
                isRefund: false,
                hasTippableSaleLine: false,
                isTipCatchAll: isTipCatchAll
            } as RegisterTipLine;

            tipLines.push(tipLine);

            return tipLine;
        }

        testTip() {
            // run this from console with meevo Register tab open:
            // angular.element('*[ng-app]').injector().get('meevo.plugins.register.RegisterTipService').testTip()

            const generateSaleLine = (
                saleType,
                employee1Id,
                employee1Percent,
                employee1AllowTips,
                employee2Id,
                employee2Percent,
                employee2AllowTips,
                baseItemPriceCharge,
                finalItemPriceCharge,
                prepaidTipRedeemed,
                serviceAllowTips,
                quantity: number = 1
            ): Model.SaleLine => {
                let saleLine = {
                    saleType: saleType,
                    quantity: quantity,
                    employeeId: employee1Id,
                    employeeSplitCommission: employee1Percent,
                    employee: {
                        id: employee1Id,
                        allowTips: employee1AllowTips,
                    },
                    secondEmployeeId: employee2Id,
                    secondEmployee: employee2Id ? {
                        id: employee2Id,
                        allowTips: employee2AllowTips,
                    } : null,
                    secondEmployeeSplitCommission: employee2Percent,
                    baseItemPriceCharge: baseItemPriceCharge,
                    finalItemPriceCharge: finalItemPriceCharge,
                    basePriceCharge: baseItemPriceCharge * quantity,
                    finalPriceCharge: finalItemPriceCharge * quantity,
                    prepaidTipRedeemed: prepaidTipRedeemed,
                    discountLines: [],
                } as Model.SaleLine;

                switch (saleType) {
                    case Meevo.Model.SalesTypeEnum.Service:
                        saleLine.item = {
                            doNotAllowTips: !serviceAllowTips
                        };
                        break;
                    case Meevo.Model.SalesTypeEnum.PackageServiceSwapFee:
                        saleLine.item = {
                            id: '00000000-0000-0000-0000-000000000001'
                        }
                        break;
                }

                return saleLine;
            }

            const expectedTipLine = (
                employeeId,
                originalEmployeeId,
                serviceTotal,
                //tippableServiceTotal,
                amount,
                percentage,
                prepaidTipMinimum
            ) => ({
                employeeId: employeeId,
                employee: null,
                originalEmployeeId: originalEmployeeId,
                originalEmployee: null,
                serviceTotal: serviceTotal,
                //tippableServiceTotal: tippableServiceTotal,
                amount: amount,
                percentage: percentage,
                prepaidTipMinimum: prepaidTipMinimum
            });

            const generateTest = (
                tipPercentage: number,
                tipAmount: number,
                useDiscountedPrice: boolean,
                includeProductsInTips: boolean,
                saleLines: Model.SaleLine[],
                expectedTipLines: Model.TipLine[],
                desc: string,
            ) => {
                return {
                    tipPercentage: tipPercentage,
                    tipAmount: tipAmount,
                    useDiscountedPrice: useDiscountedPrice,
                    includeProductsInTips: includeProductsInTips,
                    desc: desc,
                    sale: {
                        saleLines: saleLines,
                    },
                    tipLines: [] as Model.TipLine[],
                    expected: expectedTipLines,
                };
            };

            const S = Model.SalesTypeEnum.Service;
            const P = Model.SalesTypeEnum.Product;
            const U = Model.SalesTypeEnum.PackageServiceSwapFee;

            const H  = '00000000-0000-0000-0000-000000000000';
            const E1 = 'EEEEEEEE-0001-0000-0000-000000000000';
            const E2 = 'EEEEEEEE-0002-0000-0000-000000000000';
            const E3 = 'EEEEEEEE-0003-0000-0000-000000000000';
            const E4 = 'EEEEEEEE-0004-0000-0000-000000000000';

            // tip-line percentage is calculated server-side, so not included in these tests

            const tests1 = [
                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 10, /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesSingleEmployee'),

                generateTest(0.1, null, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 10, /*amount*/ 1, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesSingleEmployeePercentage'),

                generateTest(0.25, null, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,   /*base*/ 18, /*final*/ 18, /*prepaid*/ 0,  /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E2, null, true,  /*emp2*/ null, null, null,   /*base*/ 75, /*final*/ 75, /*prepaid*/ 0,  /*service-tip*/ true,  /* quantity */ 2),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 18,  /*amount*/ 4.50,  /*percent*/ null, /*prepaidTipMinimum*/ 0),
                    expectedTipLine(E2, E2, /*service-total*/ 150, /*amount*/ 37.50, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesSingleEmployeePercentageWithQuantity'),

                generateTest(0.25, null, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,   /*base*/ 18, /*final*/ 18, /*prepaid*/ 20, /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E2, null, true,  /*emp2*/ null, null, null,   /*base*/ 75, /*final*/ 75, /*prepaid*/ 0,  /*service-tip*/ true,  /* quantity */ 2),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 18,  /*amount*/ 20, /*percent*/ null, /*prepaidTipMinimum*/ 20),
                    expectedTipLine(E2, E2, /*service-total*/ 150, /*amount*/ 37.50, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesSingleEmployeePercentageWithQuantityAndPrepaid'),

                generateTest(0.1, null, false, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, false, /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(H,  E1, /*service-total*/ 15, /*amount*/ 1.5, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesBasePrice'),

                // employee has no tippable sale line, so she is excluded and the explicit tip
                // amount falls back to House - matching GetSaleTipLinesProductSaleLineAmount
                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ false),
                ],
                [
                    expectedTipLine(H,  H,  /*service-total*/ 0, /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesServiceNoTips'),

                // same as above but by percentage: the tip is derived FROM the tip lines, so an
                // empty list yields $0 and no employee at all (not even House)
                generateTest(0.1, null, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ false),
                ],
                [
                ], 'GetSaleTipLinesServiceNoTipsPercentage'),

                // mixed: E1 is on one tippable and one non-tippable service.  She stays, but only
                // the tippable line contributes to her basis (10, not 30)
                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,   /*base*/ 25, /*final*/ 20, /*prepaid*/ 0,  /*service-tip*/ false),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 10, /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesMixedTippableAndNonTippable'),

                // two employees, only one on a tippable service: the whole tip goes to E1
                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E2, null, true,  /*emp2*/ null, null, null,   /*base*/ 25, /*final*/ 20, /*prepaid*/ 0,  /*service-tip*/ false),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 10, /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesOneTippableOneNonTippableEmployee'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, false, /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(H,  E1, /*service-total*/ 10, /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesEmployeeNoTips'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 0.5,  true,  /*emp2*/ E2,   0.5,  true,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 5,  /*amount*/ 5,  /*percent*/ null, /*prepaidTipMinimum*/ 0),
                    expectedTipLine(E2, E2, /*service-total*/ 5,  /*amount*/ 5,  /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesSplit100'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 0.7,  true,  /*emp2*/ E2,   0.3,  true,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 7,  /*amount*/ 7,  /*percent*/ null, /*prepaidTipMinimum*/ 0),
                    expectedTipLine(E2, E2, /*service-total*/ 3,  /*amount*/ 3,  /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesSplitSeventyThirty'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 0.5,  true,  /*emp2*/ E2,   0.5,  true,   /*base*/ 70.99, /*final*/ 70.99, /*prepaid*/ 9.99,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 35.50,  /*amount*/ 5,  /*percent*/ null, /*prepaidTipMinimum*/ 5.00),
                    expectedTipLine(E2, E2, /*service-total*/ 35.49,  /*amount*/ 5,  /*percent*/ null, /*prepaidTipMinimum*/ 4.99),
                ], 'GetSaleTipLinesSplitUneven'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 0.5,  false, /*emp2*/ E2,   0.5,  true,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E2, E2, /*service-total*/ 10, /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesSplitFirstNoTip'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 0.5,  true,  /*emp2*/ E2,   0.5,  false,  /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 10, /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesSplitSecondNoTip'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 0.5,  false, /*emp2*/ E2,   0.5,  false,  /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(H,  E1, /*service-total*/ 5,  /*amount*/ 5,  /*percent*/ null, /*prepaidTipMinimum*/ 0),
                    expectedTipLine(H,  E2, /*service-total*/ 5,  /*amount*/ 5,  /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesSplitNoTip'),

                generateTest(0.10, null, true, false, [
                    generateSaleLine(P, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                ], 'GetSaleTipLinesProductSaleLinePercentage'),

                generateTest(0.10, null, true, true, [
                    generateSaleLine(P, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 10, /*amount*/ 1,    /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesProductSaleLinePercentageWithProductTipping'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(P, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(H,  H,  /*service-total*/ 0,  /*amount*/ 10,   /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesProductSaleLineAmount'),

                generateTest(null, 10, true, true, [
                    generateSaleLine(P, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 10,  /*amount*/ 10,  /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesProductSaleLineAmountWithProductTipping'),

                generateTest(null, 12, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E2, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 20, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 10, /*amount*/ 4,  /*percent*/ null, /*prepaidTipMinimum*/ 0),
                    expectedTipLine(E2, E2, /*service-total*/ 20, /*amount*/ 8,  /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesTwoSaleLines'),

                generateTest(null, 8, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 3,  /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E2, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 20, /*prepaid*/ 0,  /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E3, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 30, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 10, /*amount*/ 3,  /*percent*/ null, /*prepaidTipMinimum*/ 3),
                    expectedTipLine(E2, E2, /*service-total*/ 20, /*amount*/ 2,  /*percent*/ null, /*prepaidTipMinimum*/ 0),
                    expectedTipLine(E3, E3, /*service-total*/ 30, /*amount*/ 3,  /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesMultipleSaleLinesPrepaid'),

                generateTest(null, 8, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 0.5,  true,  /*emp2*/ E4,   0.5,  false,  /*base*/ 15, /*final*/ 10, /*prepaid*/ 3,  /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E2, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 20, /*prepaid*/ 0,  /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E3, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 30, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 10, /*amount*/ 3,  /*percent*/ null, /*prepaidTipMinimum*/ 3),
                    expectedTipLine(E2, E2, /*service-total*/ 20, /*amount*/ 2,  /*percent*/ null, /*prepaidTipMinimum*/ 0),
                    expectedTipLine(E3, E3, /*service-total*/ 30, /*amount*/ 3,  /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesMultipleSaleLinesSplitNoTipPrepaid'),

                generateTest(null, 60, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 3,  /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E2, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 20, /*prepaid*/ 0,  /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E3, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 30, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 10, /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 3),
                    expectedTipLine(E2, E2, /*service-total*/ 20, /*amount*/ 20, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                    expectedTipLine(E3, E3, /*service-total*/ 30, /*amount*/ 30, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesMultipleSaleLinesPrepaidLess'),

                generateTest(null, 8, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 3,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 10, /*amount*/ 8, /*percent*/ null, /*prepaidTipMinimum*/ 3),
                ], 'GetSaleTipLinesSplitSecondNoTip'),

                generateTest(null, 8, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 3,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 10,  /*amount*/ 8, /*percent*/ null, /*prepaidTipMinimum*/ 3),
                ], 'GetSaleTipLinesOnePrepaid'),

                generateTest(null, 4, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 3,  /*service-tip*/ false),
                    generateSaleLine(S, /*emp1*/ E2, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 0,  /*amount*/ 3, /*percent*/ null, /*prepaidTipMinimum*/ 3),
                    expectedTipLine(E2, E2, /*service-total*/ 10, /*amount*/ 1, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesOnePrepaidNoTipOnReg'),

                generateTest(null, 4, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 3,  /*service-tip*/ false),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 0,  /*amount*/ 4, /*percent*/ null, /*prepaidTipMinimum*/ 3),
                ], 'GetSaleTipLinesNoServiceTip'),

                generateTest(null, 59, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 0.5,  true,  /*emp2*/ E2,   0.5,  false,  /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E2, 0.5,  false, /*emp2*/ E3,   0.5,  false,  /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E1, 0.5,  true,  /*emp2*/ E2,   0.5,  false,  /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ false),
                    generateSaleLine(S, /*emp1*/ E1, 0.5,  true,  /*emp2*/ E2,   0.5,  false,  /*base*/ 15, /*final*/ 10, /*prepaid*/ 2,  /*service-tip*/ false),
                    generateSaleLine(S, /*emp1*/ E1, 0.5,  true,  /*emp2*/ E4,   0.5,  true,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 2,  /*service-tip*/ false),
                    generateSaleLine(S, /*emp1*/ E2, 0.5,  false, /*emp2*/ E3,   0.5,  false,  /*base*/ 15, /*final*/ 10, /*prepaid*/ 5,  /*service-tip*/ false),
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 10, /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 1,  /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E1, 0.5,  true,  /*emp2*/ E4,   0.5,  true,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 20, /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E1, 0.5,  true,  /*emp2*/ E4,   0.5,  true,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 40, /*amount*/ 38.16, /*percent*/ null, /*prepaidTipMinimum*/ 24),
                    expectedTipLine(H,  E2, /*service-total*/ 5,  /*amount*/ 4.92,  /*percent*/ null, /*prepaidTipMinimum*/ 2.5),
                    expectedTipLine(H,  E3, /*service-total*/ 5,  /*amount*/ 4.92,  /*percent*/ null, /*prepaidTipMinimum*/ 2.5),
                    expectedTipLine(E4, E4, /*service-total*/ 10, /*amount*/ 11,    /*percent*/ null, /*prepaidTipMinimum*/ 11),
                ], 'GetSaleTipsMega'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 0.5,  false, /*emp2*/ E2,   0.5,  false,  /*base*/ 15, /*final*/ 10, /*prepaid*/ 5,  /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E1, 0.5,  false, /*emp2*/ E3,   0.5,  false,  /*base*/ 15, /*final*/ 10, /*prepaid*/ 5,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(H, E1, /*service-total*/ 10, /*amount*/ 5, /*percent*/ null, /*prepaidTipMinimum*/ 5),
                    expectedTipLine(H, E2, /*service-total*/ 5,  /*amount*/ 2.5, /*percent*/ null, /*prepaidTipMinimum*/ 2.5),
                    expectedTipLine(H, E3, /*service-total*/ 5,  /*amount*/ 2.5, /*percent*/ null, /*prepaidTipMinimum*/ 2.5),
                ], 'GetSaleTipLinesHouseWithMultipleEmployees'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 0.5,  false, /*emp2*/ E2,   0.5,  false,  /*base*/ 15, /*final*/ 10, /*prepaid*/ 5,  /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E1, 0.5,  false, /*emp2*/ E2,   0.5,  false,  /*base*/ 15, /*final*/ 10, /*prepaid*/ 5,  /*service-tip*/ false),
                ],
                [
                    expectedTipLine(H, E1, /*service-total*/ 5,  /*amount*/ 5, /*percent*/ null, /*prepaidTipMinimum*/ 5),
                    expectedTipLine(H, E2, /*service-total*/ 5,  /*amount*/ 5, /*percent*/ null, /*prepaidTipMinimum*/ 5),
                ], 'GetSaleTipLinesHouseWithMultipleEmployeesAndNoTipService'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 0.5,  false, /*emp2*/ E2,   0.5,  false,  /*base*/ 15, /*final*/ 10, /*prepaid*/ 5,  /*service-tip*/ false),
                ],
                [
                    expectedTipLine(H, E1, /*service-total*/ 0,  /*amount*/ 5, /*percent*/ null, /*prepaidTipMinimum*/ 2.5),
                    expectedTipLine(H, E2, /*service-total*/ 0,  /*amount*/ 5, /*percent*/ null, /*prepaidTipMinimum*/ 2.5),
                ], 'GetSaleTipLinesHouseWithOneEmployeesAndNoTipService'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true, /*emp2*/ null, null, null,   /*base*/ 10, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E2, null, true, /*emp2*/ null, null, null,   /*base*/ 30, /*final*/ 30, /*prepaid*/ 0,  /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E1, null, true, /*emp2*/ null, null, null,   /*base*/ 10, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 20, /*amount*/ 4, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                    expectedTipLine(E2, E2, /*service-total*/ 30, /*amount*/ 6, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesRepeatedEmployee'),

                generateTest(null, 0, true, false, [
                    generateSaleLine(S, /*emp1*/ H,  null, true, /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(H, H,  /*service-total*/ 10, /*amount*/ 0, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesHouseEmployeeWithoutTip'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ H,  null, true, /*emp2*/ null, null, null,   /*base*/ 15, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(H, H,  /*service-total*/ 10, /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesHouseEmployee'),

                // TODO: add way to test with existing tip lines

                generateTest(0.1, null, true, false, [
                    generateSaleLine(S, /*emp1*/ E1,  null, true, /*emp2*/ null, null, null,   /*base*/ 200, /*final*/ 200, /*prepaid*/ 50, /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E2,  null, true, /*emp2*/ null, null, null,   /*base*/ 100, /*final*/ 100, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1,  /*service-total*/ 200, /*amount*/ 50, /*percent*/ null, /*prepaidTipMinimum*/ 50),
                    expectedTipLine(E2, E2,  /*service-total*/ 100, /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesMultipleEmployeesPrepaidByPercentageInsufficient'),

                generateTest(0.1, null, true, false, [
                    generateSaleLine(S, /*emp1*/ E1,  null, true, /*emp2*/ null, null, null,   /*base*/ 200, /*final*/ 200, /*prepaid*/ 20, /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E2,  null, true, /*emp2*/ null, null, null,   /*base*/ 100, /*final*/ 100, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1,  /*service-total*/ 200, /*amount*/ 20, /*percent*/ null, /*prepaidTipMinimum*/ 20),
                    expectedTipLine(E2, E2,  /*service-total*/ 100, /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesMultipleEmployeesPrepaidByPercentageSufficient'),

                generateTest(null, 50, true, false, [
                    generateSaleLine(S, /*emp1*/ E1,  null, true, /*emp2*/ null, null, null,   /*base*/ 200, /*final*/ 200, /*prepaid*/ 50, /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E2,  null, true, /*emp2*/ null, null, null,   /*base*/ 100, /*final*/ 100, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1,  /*service-total*/ 200, /*amount*/ 50, /*percent*/ null, /*prepaidTipMinimum*/ 50),
                    expectedTipLine(E2, E2,  /*service-total*/ 100, /*amount*/ 0,  /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesMultipleEmployeesPrepaidByAmount'),

                generateTest(null, 75, true, false, [
                    generateSaleLine(S, /*emp1*/ E1,  null, true, /*emp2*/ null, null, null,   /*base*/ 200, /*final*/ 200, /*prepaid*/ 50, /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E2,  null, true, /*emp2*/ null, null, null,   /*base*/ 100, /*final*/ 100, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1,  /*service-total*/ 200, /*amount*/ 50, /*percent*/ null, /*prepaidTipMinimum*/ 50),
                    expectedTipLine(E2, E2,  /*service-total*/ 100, /*amount*/ 25, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], 'GetSaleTipLinesMultipleEmployeesPrepaidByAmountOver'),
            ];

            const tests2 = [
                generateTest(null, null, true, false, [], [], 'no tip'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 10, /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], '1 saleline - $10 | discounted service total ($10) | E1/E1: $10'),

                generateTest(0.1, null, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 10, /*amount*/ 1,  /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], '1 saleline - 10% | discounted service total ($10) | E1/E1: $1'),

                generateTest(null, 10, false, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 20, /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], '1 saleline - $10 | non-discounted service total ($20) | E1/E1: $10'),

                generateTest(null, 10, false, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 20, /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], '1 saleline - 10% | non-discounted service total ($20) | E1/E1: $2'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, false, /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(H, E1, /*service-total*/ 10, /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], '1 saleline - $10 | employee-to-house tip | H/E1 (employee-no-tip): $10'),

                // E1 has no tippable sale line, so she is pruned and the explicit tip amount
                // falls back to House
                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ false),
                ],
                [
                    expectedTipLine(H,  H,  /*service-total*/ 0,  /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], '1 saleline - $10 | no service total | H/H (service-no-tip): $10'),

                // both employees are pruned, so the tip is no longer divided - House takes it all
                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ false),
                    generateSaleLine(S, /*emp1*/ E2, null, true,  /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ false),
                ],
                [
                    expectedTipLine(H,  H,  /*service-total*/ 0,  /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], '1 saleline - $10 | multiple employees, no service total | H/H (service-no-tip): $10'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, false,  /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ false),
                    generateSaleLine(S, /*emp1*/ E2, null, false,  /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ false),
                ],
                [
                    expectedTipLine(H,  H, /*service-total*/ 0,  /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], '1 saleline - $10 | multiple employees, no service total, no employee tip, convert-to-house | H/H: $10'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E2, null, true,  /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 10, /*amount*/ 5, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                    expectedTipLine(E2, E2, /*service-total*/ 10, /*amount*/ 5, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], '2 salelines - $10 | proportional tip ($10/$10) | E1/E1: $5, E2/E2: $5'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,  /*base*/ 30, /*final*/ 30, /*prepaid*/ 0,  /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E2, null, true,  /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 30, /*amount*/ 7.5, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                    expectedTipLine(E2, E2, /*service-total*/ 10, /*amount*/ 2.5, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], '2 salelines - $10 | proportional tip ($30/$10) | E1/E1: $7.50, E2/E2: $2.50'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, false, /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E2, null, true,  /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(H,  E1, /*service-total*/ 10, /*amount*/ 5, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                    expectedTipLine(E2, E2, /*service-total*/ 10, /*amount*/ 5, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], '2 salelines - $10 | employee-to-house, proportional tip ($10/$10) | H/E1 (employee-no-tip): $5, E2/E2: $5'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, false, /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E2, null, false, /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(H, E1, /*service-total*/ 10, /*amount*/ 5, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                    expectedTipLine(H, E2, /*service-total*/ 10, /*amount*/ 5, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], '2 salelines - $10 | all employee-no-tip, employee-to-house combined | H/E1 (employee-no-tip): $5, H/E2: $5 (employee-no-tip)'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                    generateSaleLine(S, /*emp1*/ E2, null, true,  /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ false),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 10, /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], '2 salelines - $10 | multiple employees, at least one with service total, proportional tip | E1/E1: $10'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, true,  /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ false),
                    generateSaleLine(S, /*emp1*/ E2, null, true,  /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ false),
                ],
                [
                    expectedTipLine(H,  H,  /*service-total*/ 0,  /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], '2 salelines - $10 | multiple employees, all no service tip | H/H: $10'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, null, false, /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ false),
                    generateSaleLine(S, /*emp1*/ E2, null, false, /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ false),
                ],
                [
                    expectedTipLine(H, H, /*service-total*/ 0,  /*amount*/ 10,  /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], '2 salelines - $10 | multiple employees, all no service tip, all employee no tip, employee-to-house combined | H: $10)'),

                // SPLIT WITHOUT PREPAID

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 0.7,  true,   /*emp2*/ E2,   0.3,  true,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 7,  /*amount*/ 7, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                    expectedTipLine(E2, E2, /*service-total*/ 3,  /*amount*/ 3, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], '1 saleline - $10 | split 70/30 | proportional tip | E1/E1: $7, E2/E2: $3'),

                generateTest(0.1, null, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 0.7,  true,   /*emp2*/ E2,   0.3,  true,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 7,  /*amount*/ 0.7, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                    expectedTipLine(E2, E2, /*service-total*/ 3,  /*amount*/ 0.3, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], '1 saleline - 10% | split 70/30 | proportional tip | E1/E1: $0.70, E2/E2: $0.30'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 1,    true,   /*emp2*/ E2,   1,    true,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 5,  /*amount*/ 5, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                    expectedTipLine(E2, E2, /*service-total*/ 5,  /*amount*/ 5, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], '1 saleline - $10 | split full | proportional tip | E1/E1: $5, E2/E2: $5'),

                generateTest(0.1, null, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 1,    true,   /*emp2*/ E2,   1,    true,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 5,  /*amount*/ 0.5, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                    expectedTipLine(E2, E2, /*service-total*/ 5,  /*amount*/ 0.5, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], '1 saleline - 10% | split full | proportional tip | E1/E1: $0.50, E2/E2: $0.50'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 1,    true,   /*emp2*/ E2,   1,    false, /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 10, /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], '1 saleline - $10 | split full | proportional tip | E1/E1: $10'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 1,    false,  /*emp2*/ E2,   1,    true,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E2, E2, /*service-total*/ 10, /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], '1 saleline - $10 | split full | proportional tip | E2/E2: $10'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 1,    false,  /*emp2*/ E2,   1,    false, /*base*/ 20, /*final*/ 10, /*prepaid*/ 0,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(H, E1, /*service-total*/ 5, /*amount*/ 5, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                    expectedTipLine(H, E2, /*service-total*/ 5, /*amount*/ 5, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], '1 saleline - $10 | split full | proportional tip | E1 (no-employee-tip), E2 (no-employee-tip) -> H/E1: $5, H/E2: $5'),

                // SPLIT WITH PREPAID

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 0.7,  true,   /*emp2*/ E2,   0.3,  true,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 10,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 7,  /*amount*/ 7, /*percent*/ null, /*prepaidTipMinimum*/ 7),
                    expectedTipLine(E2, E2, /*service-total*/ 3,  /*amount*/ 3, /*percent*/ null, /*prepaidTipMinimum*/ 3),
                ], '1 saleline - $10 | split 70/30, prepaid $10 | proportional tip | E1/E1: $7 (prepaid), E2/E2: $3 (prepaid)'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 0.7,  false,  /*emp2*/ E2,   0.3,  true,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 10,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E2, E2, /*service-total*/ 10, /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 10),
                ], '1 saleline - $10 | split 70/30, prepaid $10 | proportional tip | E2/E2: $10 (prepaid)'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 0.7,  true,   /*emp2*/ E2,   0.3,  false, /*base*/ 20, /*final*/ 10, /*prepaid*/ 10,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 10, /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 10),
                ], '1 saleline - $10 | split 70/30, prepaid $10 | proportional tip | E1/E1: $10 (prepaid)'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 1,    true,   /*emp2*/ E2,   1,    true,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 10,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 5,  /*amount*/ 5, /*percent*/ null, /*prepaidTipMinimum*/ 5),
                    expectedTipLine(E2, E2, /*service-total*/ 5,  /*amount*/ 5, /*percent*/ null, /*prepaidTipMinimum*/ 5),
                ], '1 saleline - $10 | split full, prepaid $10 | proportional tip | E1/E1: $5 (prepaid), E2/E2: $5'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 1,    true,   /*emp2*/ E2,   1,    false, /*base*/ 20, /*final*/ 10, /*prepaid*/ 10,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 10, /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 10),
                ], '1 saleline - $10 | split full, prepaid $10 | proportional tip | E1/E1: $10 (prepaid)'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 1,    false,  /*emp2*/ E2,   1,    true,  /*base*/ 20, /*final*/ 10, /*prepaid*/ 10,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E2, E2, /*service-total*/ 10, /*amount*/ 10, /*percent*/ null, /*prepaidTipMinimum*/ 10),
                ], '1 saleline - $10 | split full, prepaid $10 | proportional tip | E2/E2: $10 (prepaid)'),

                generateTest(null, 10, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 1,    false,  /*emp2*/ E2,   1,    false, /*base*/ 20, /*final*/ 10, /*prepaid*/ 10,  /*service-tip*/ true),
                ],
                [
                    expectedTipLine(H, E1, /*service-total*/ 5, /*amount*/ 5, /*percent*/ null, /*prepaidTipMinimum*/ 5),
                    expectedTipLine(H, E2, /*service-total*/ 5, /*amount*/ 5, /*percent*/ null, /*prepaidTipMinimum*/ 5),
                ], '1 saleline - $10 | split full, prepaid $10 | proportional tip | E1 (no-employee-tip), E2 (no-employee-tip) -> H/E1: $5, H/E2: $5'),
            ];

            const tests3 = [
                generateTest(null, 0, true, false, [
                    generateSaleLine(S, /*emp1*/ E1, 0.6,  true,   /*emp2*/ E2,   0.4,  true,  /*base*/ 60, /*final*/ 40, /*prepaid*/ 0,   /*service-tip*/ true),
                    generateSaleLine(U, /*emp1*/ E1, 1,    true,   /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 16, /*prepaid*/ 0,   /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 40, /*amount*/ 0, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                    expectedTipLine(E2, E2, /*service-total*/ 16, /*amount*/ 0, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], '2 salelines - $0 | split 60/40 | discounted | E1/E1: $24+$16 = $40, E2/E2: $16+$0 = $16'),

                generateTest(null, 0, false, false, [
                    generateSaleLine(S, /*emp1*/ E1, 0.6,  true,   /*emp2*/ E2,   0.4,  true,  /*base*/ 60, /*final*/ 40, /*prepaid*/ 0,   /*service-tip*/ true),
                    generateSaleLine(U, /*emp1*/ E1, 1,    true,   /*emp2*/ null, null, null,  /*base*/ 20, /*final*/ 16, /*prepaid*/ 0,   /*service-tip*/ true),
                ],
                [
                    expectedTipLine(E1, E1, /*service-total*/ 56, /*amount*/ 0, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                    expectedTipLine(E2, E2, /*service-total*/ 24, /*amount*/ 0, /*percent*/ null, /*prepaidTipMinimum*/ 0),
                ], '2 salelines - $0 | split 60/40 | non-discounted | E1/E1: $36+$20 = $56, E2/E2: $24+$0 = $24'),
            ];

            const testResults = [];

            const registerController = angular.element('#register').controller() as Meevo.Plugins.Register.RegisterController;
            if (!registerController) {
                throw "open Register tab and re-run command";
            }
            const registerContext = registerController.context;

            const executeTest = (test) => {
                this.generateTipEmployeeLines(registerContext, test.sale, test.tipLines, test.includeProductsInTips);

                let totalPrepaidTip = 0;
                for (const saleLine of test.sale.saleLines) {
                    totalPrepaidTip = registerContext.addNormalizedNumbers(totalPrepaidTip, saleLine.prepaidTipRedeemed || 0);
                }

                test.sale.tipLines = test.tipLines;
                test.sale.tipAmount = test.tipAmount;
                test.sale.tipPercentage = test.tipPercentage;
                test.sale.useDiscountedPrice = test.useDiscountedPrice;
                test.sale.totalPrepaidTipRedeemed = totalPrepaidTip;

                let tipAmount = this.getTipAmount(registerContext, test.sale);

                if (totalPrepaidTip > 0) {
                    this.calculatePrepaidTipMinimums(registerContext, test.tipLines, test.sale.saleLines);
                }

                // mirrors rebuildTipLines: employees with no tippable sale line are removed before
                // the tip is distributed, so an explicit tip amount can fall back to House
                this.pruneNonTippableEmployees(test.sale, test.tipLines);

                _(test.tipLines).forEach((tipLine) => {
                    tipLine.serviceTotal = this.calculateServicePrice(tipLine.baseTotal, tipLine.finalTotal, test.useDiscountedPrice);
                });

                if (tipAmount > 0) {
                    this.distributeTipIntoTipLines(registerContext, test.tipLines, tipAmount);
                }

                testResults.push({
                    desc: test.desc,
                    success: isSuccess(test) ? '✅' : '❌',
                });
            };

            const isSuccess = (test) => {
                const actual = test.tipLines
                    .filter(tipLine => tipLine.amount > 0 || tipLine.serviceTotal > 0 || tipLine.prepaidTipMinimum > 0)
                    .map(tipLine => ({
                        employeeId: tipLine.employeeId,
                        employee: null,
                        originalEmployeeId: tipLine.originalEmployeeId,
                        originalEmployee: null,
                        serviceTotal: tipLine.serviceTotal,
                        amount: tipLine.amount,
                        percentage: tipLine.percentage,
                        prepaidTipMinimum: tipLine.prepaidTipMinimum,
                    }));

                const result = _.isEqual(actual, test.expected);
                if (!result) {
                    console.error('actual', actual, 'expected', test.expected);
                }

                return result;
            };

            const tests = [...tests1, ...tests2, ...tests3];
            tests.forEach(test => executeTest(test));

            console.table(testResults);
        }

        static factory(): Function {
            let svc = (
                $q,
                $translate,
                dataService,
                modalService,
                busyService
            ) => new RegisterTipService(
                $q,
                $translate,
                dataService,
                modalService,
                busyService
            );

            svc.$inject = [
                "$q",
                "$translate",
                "meevo.components.common.DataService",
                "meevo.components.modal.ModalService",
                "meevo.components.busy.BusyService",
            ];

            return svc;
        }
    }

    angular.module("meevo.plugins.register")
        .factory("meevo.plugins.register.RegisterTipService", RegisterTipService.factory());
}
